import { useEffect, useMemo, useState } from "react";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import Notification from "../../../components/ui/Notification";
import { MdExpandMore, MdSave, MdUpload } from "react-icons/md";
import {
  updateContentText,
  uploadMedia,
  attachMediaToContent,
  replaceContentMedia,
} from "../../../api/content";

const LANGS = ["en", "ar", "ku"];

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const mediaBase = API_BASE?.replace("/api", "") || "";

/* =========================
   HELPERS
========================= */

const resolveMediaSrc = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/uploads")) return `${mediaBase}${path}`;
  return `${mediaBase}/${path.replace(/^\/?/, "")}`;
};

const isVideo = (path) =>
  typeof path === "string" &&
  (path.endsWith(".mp4") || path.endsWith(".webm"));

const titleize = (str) =>
  (str || "")
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());

const normalizeKey = (value) =>
  (value || "")
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .toLowerCase();

const PROTOCOL_SUFFIXES = [
  "id",
  "title",
  "machine",
  "protocol-overview",
  "what-is-included",
  "sessions",
  "treatment-time",
  "best-suited-for",
  "video-embed-url",
  "video-url-embedded",
  "videoembedurl",
  "videourlembedded",
];

const getProtocolIdFromKey = (rawKey) => {
  const key = normalizeKey(rawKey);
  for (const suffix of PROTOCOL_SUFFIXES) {
    const token = `-${suffix}`;
    if (key.endsWith(token)) {
      return key.slice(0, -token.length);
    }
  }
  return null;
};

const toProtocolLabel = (id) =>
  (id || "")
    .split("-")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");

/* =========================
   COMPONENT
========================= */

const ContentEditor = ({ items = [], page, section, onSaved }) => {
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [form, setForm] = useState({});
  const [openProtocolCards, setOpenProtocolCards] = useState({});

  /* =========================
     BUILD FORM (FIXED)
  ========================= */

  useEffect(() => {
    const obj = {};

    items.forEach((item) => {
      const media = Array.isArray(item.media)
        ? item.media[0]
        : item.media || null;

      obj[item.id] = {
        key: item.key,
        mediaId: media?.mediaAssetId || media?.id || null,
        mediaPath: media?.filePath || "",
        values: {
          en:
            item.translations?.find((t) => t.language === "en")
              ?.value || "",
          ar:
            item.translations?.find((t) => t.language === "ar")
              ?.value || "",
          ku:
            item.translations?.find((t) => t.language === "ku")
              ?.value || "",
        },
      };
    });

    setForm(obj);
  }, [items]);

  const entries = useMemo(() => Object.entries(form), [form]);
  const isProtocolsCore = page === "protocols" && section === "core";
  const groupedEntries = useMemo(() => {
    if (!isProtocolsCore) {
      return [{ groupName: "All Fields", items: entries }];
    }

    const groups = new Map();
    const protocolIds = Array.from(
      new Set(
        entries
          .map(([, item]) => getProtocolIdFromKey(item.key))
          .filter(Boolean)
      )
    );

    entries.forEach(([itemId, item]) => {
      const key = normalizeKey(item.key);
      let groupName = "General";

      if (key.startsWith("intro-")) {
        groupName = "Intro";
      } else {
        const matchedId = protocolIds.find((id) => key.startsWith(`${id}-`));
        if (matchedId) {
          groupName = `Protocol: ${toProtocolLabel(matchedId)}`;
        }
      }

      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName).push([itemId, item]);
    });

    return Array.from(groups.entries()).map(([groupName, groupedItems]) => ({
      groupName,
      items: groupedItems,
    }));
  }, [entries, isProtocolsCore]);

  useEffect(() => {
    if (!isProtocolsCore) return;
    setOpenProtocolCards(
      groupedEntries.reduce((acc, group) => {
        acc[group.groupName] = true;
        return acc;
      }, {})
    );
  }, [groupedEntries, isProtocolsCore]);

  /* =========================
     TEXT CHANGE
  ========================= */

  const handleTextChange = (itemId, value) => {
    setForm((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        values: {
          ...prev[itemId].values,
          [activeLang]: value,
        },
      },
    }));
  };

  /* =========================
     MEDIA REPLACE
  ========================= */

  const handleMediaReplace = async (itemId, file) => {
    try {
      setNotification({
        type: "info",
        title: "Uploading",
        message: "Replacing media...",
      });

      const item = form[itemId];
      let response;

      if (item.mediaId) {
        response = await replaceContentMedia(
          itemId,
          item.mediaId,
          file,
          { page: "home", section: "hero" }
        );
      } else {
        const upload = await uploadMedia(file, {
          page: "home",
          section: "hero",
        });

        await attachMediaToContent(itemId, upload.data.id);
        response = upload;
      }

      const media = response.data.media ?? response.data;

      setForm((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          mediaId: media.id,
          mediaPath: media.filePath,
        },
      }));

      setNotification({
        type: "success",
        title: "Updated",
        message: "Media updated successfully",
      });
    } catch {
      setNotification({
        type: "error",
        title: "Error",
        message: "Media update failed",
      });
    }
  };

  /* =========================
     SAVE
  ========================= */

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const itemId of Object.keys(form)) {
        const translations = LANGS.map((lang) => ({
          language: lang,
          value: form[itemId].values[lang],
        }));

        await updateContentText(itemId, { translations });
      }

      setNotification({
        type: "success",
        title: "Saved",
        message: "Section updated successfully",
      });

      setTimeout(() => onSaved?.(), 600);
    } catch {
      setNotification({
        type: "error",
        title: "Error",
        message: "Save failed",
      });
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     RENDER (FIXED)
  ========================= */

  return (
    <>
      {notification && (
        <div className="fixed top-6 right-6 z-[9999]">
          <Notification
            {...notification}
            onClose={() => setNotification(null)}
          />
        </div>
      )}

      <div className="space-y-6">
        {/* LANGUAGE SWITCH */}
        <div className="flex gap-2 border-b pb-2">
          {LANGS.map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveLang(lang)}
              className={`px-3 py-1 rounded-md text-xs font-semibold uppercase ${
                activeLang === lang
                  ? "bg-[#95785A] text-white"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {lang}
            </button>
          ))}
        </div>

        {!isProtocolsCore &&
          groupedEntries.map((group) => (
            <section key={group.groupName} className="border rounded-xl p-3 bg-white space-y-3">
              {group.groupName !== "All Fields" ? (
                <div className="text-sm font-semibold text-gray-700 border-b pb-2">
                  {group.groupName}
                </div>
              ) : null}
              {group.items.map(([itemId, item]) => {
                const src = item.mediaPath
                  ? resolveMediaSrc(item.mediaPath)
                  : null;

                return (
                  <div
                    key={itemId}
                    className="border rounded-lg p-3 space-y-3 bg-gray-50"
                  >
                    <div className="text-sm font-semibold">
                      {titleize(item.key)}
                    </div>

                    {item.mediaPath && (
                      <div className="flex items-start gap-4">
                        {isVideo(item.mediaPath) ? (
                          <video
                            src={src}
                            controls
                            preload="metadata"
                            className="w-48 rounded-md bg-black"
                          />
                        ) : (
                          <img
                            src={src}
                            alt=""
                            className="w-40 h-28 object-cover rounded-md border"
                          />
                        )}

                        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-blue-600">
                          <MdUpload />
                          Replace
                          <input
                            type="file"
                            hidden
                            onChange={(e) =>
                              e.target.files &&
                              handleMediaReplace(itemId, e.target.files[0])
                            }
                          />
                        </label>
                      </div>
                    )}

                    <Input
                      label={`${titleize(item.key)} (${activeLang})`}
                      value={item.values[activeLang]}
                      textarea
                      rows={3}
                      onChange={(e) =>
                        handleTextChange(itemId, e.target.value)
                      }
                    />
                  </div>
                );
              })}
            </section>
          ))}

        {isProtocolsCore && (
          <section className="space-y-4">
            {groupedEntries.map((group) => (
              <article
                key={group.groupName}
                className="border rounded-xl bg-white overflow-hidden"
              >
                <button
                  type="button"
                  className="w-full px-4 py-3 border-b bg-gray-50 flex items-center justify-between text-left"
                  onClick={() =>
                    setOpenProtocolCards((prev) => ({
                      ...prev,
                      [group.groupName]: !prev[group.groupName],
                    }))
                  }
                >
                  <h3 className="text-sm font-semibold text-gray-800">
                    {group.groupName}
                  </h3>
                  <MdExpandMore
                    size={22}
                    className={`transition-transform ${
                      openProtocolCards[group.groupName] ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {openProtocolCards[group.groupName] && (
                  <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {group.items.map(([itemId, item]) => {
                      const src = item.mediaPath
                        ? resolveMediaSrc(item.mediaPath)
                        : null;

                      return (
                        <div
                          key={itemId}
                          className="border rounded-lg p-3 bg-gray-50 space-y-3"
                        >
                          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            {titleize(item.key)}
                          </div>

                          {item.mediaPath && (
                            <div className="space-y-2">
                              {isVideo(item.mediaPath) ? (
                                <video
                                  src={src}
                                  controls
                                  preload="metadata"
                                  className="w-full max-w-xs rounded-md bg-black"
                                />
                              ) : (
                                <img
                                  src={src}
                                  alt=""
                                  className="w-full max-w-xs h-32 object-cover rounded-md border"
                                />
                              )}

                              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-blue-600">
                                <MdUpload />
                                Replace
                                <input
                                  type="file"
                                  hidden
                                  onChange={(e) =>
                                    e.target.files &&
                                    handleMediaReplace(itemId, e.target.files[0])
                                  }
                                />
                              </label>
                            </div>
                          )}

                          <Input
                            label={`${titleize(item.key)} (${activeLang})`}
                            value={item.values[activeLang]}
                            textarea
                            rows={4}
                            onChange={(e) =>
                              handleTextChange(itemId, e.target.value)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            ))}
          </section>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            <MdSave className="mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>

        
      </div>
    </>
  );

  
};

export default ContentEditor;
